import { useEffect, useMemo, useState } from "react";
import { api, API_BASE, formatApiErrorDetail, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  BookOpen,
  Download,
  Plus,
  Trash2,
  ArrowDownToLine,
  ArrowUpFromLine,
  Package,
  Calendar,
  X,
  Eye,
  Pencil,
  FileText,
  Paperclip,
  ExternalLink,
} from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const monthStartISO = () => {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0, 10);
};
const yearStartISO = () => `${new Date().getFullYear()}-01-01`;

const UNITATI_OPTIONS = ["g", "kg", "ml", "l", "buc"];

const EMPTY = {
  tip: "intrare",
  data: todayISO(),
  data_fabricare: "",
  data_expirare: "",
  cantitate: "",
  unitate: "buc",
  nr_lot: "",
  aviz: "",
  firma: "",
  referinta: "",
  observatii: "",
  aviz_pdf_url: "",
  ssa_pdf_url: "",
};

/**
 * Fișă de Magazie — vedere globală tip Excel:
 *  - Fiecare substanță = un „tab" (ca sheet-urile din Excel: SUPER KILLER, SOLFAC TRIO...)
 *  - Tabelul are exact aceleași coloane: DATA / INTRĂRI / IEȘIRI / STOC / NR. LOT / Aviz Bio / U.M. / Firma
 */
export default function FiseMagazie() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stocks, setStocks] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [movements, setMovements] = useState([]);
  const [soldInitial, setSoldInitial] = useState(null);
  const [soldInitialData, setSoldInitialData] = useState(null);
  const [soldCurent, setSoldCurent] = useState(null);
  const [ultimaMiscareData, setUltimaMiscareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMov, setLoadingMov] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  // Date range filter (empty = no filter)
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [viewMov, setViewMov] = useState(null); // Movement being viewed in detail dialog
  const [editingMov, setEditingMov] = useState(null); // Movement being edited

  const active = useMemo(
    () => stocks.find((s) => s.id === activeId) || null,
    [stocks, activeId]
  );

  const loadStocks = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/stocks");
      setStocks(data);
      if (data.length > 0 && !activeId) setActiveId(data[0].id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    }
    setLoading(false);
  };

  const loadMovements = async (id) => {
    if (!id) return;
    setLoadingMov(true);
    try {
      const params = {};
      if (startDate) params.start = startDate;
      if (endDate) params.end = endDate;
      const { data } = await api.get(`/stocks/${id}/movements`, { params });
      setMovements(data.movements || []);
      setSoldInitial(data.sold_initial);
      setSoldInitialData(data.sold_initial_data);
      setSoldCurent(data.sold_curent);
      setUltimaMiscareData(data.ultima_miscare_data);
      setStocks((prev) => prev.map((s) => (s.id === id ? { ...s, ...data.stock } : s)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    }
    setLoadingMov(false);
  };

  useEffect(() => { loadStocks(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (activeId) loadMovements(activeId); /* eslint-disable-next-line */ }, [activeId, startDate, endDate]);

  const setPreset = (preset) => {
    const today = todayISO();
    if (preset === "today") { setStartDate(today); setEndDate(today); }
    else if (preset === "7") { setStartDate(daysAgoISO(6)); setEndDate(today); }
    else if (preset === "30") { setStartDate(daysAgoISO(29)); setEndDate(today); }
    else if (preset === "month") { setStartDate(monthStartISO()); setEndDate(today); }
    else if (preset === "year") { setStartDate(yearStartISO()); setEndDate(today); }
    else if (preset === "clear") { setStartDate(""); setEndDate(""); }
  };

  const totals = useMemo(() => {
    let intrari = 0, iesiri = 0;
    for (const r of movements) {
      const q = Number(r.cantitate) || 0;
      if (r.tip === "intrare") intrari += q; else iesiri += q;
    }
    return { intrari, iesiri };
  }, [movements]);

  const openEditMov = (m) => {
    setEditingMov(m);
    setForm({
      tip: m.tip || "intrare",
      data: m.data || todayISO(),
      data_fabricare: m.data_fabricare || "",
      data_expirare: m.data_expirare || "",
      cantitate: String(m.cantitate ?? ""),
      unitate: m.unitate || active?.unitate || "buc",
      nr_lot: m.nr_lot || "",
      aviz: m.aviz || "",
      firma: m.firma || "",
      referinta: m.referinta_numar || "",
      observatii: m.observatii || "",
      aviz_pdf_url: m.aviz_pdf_url || "",
      ssa_pdf_url: m.ssa_pdf_url || "",
    });
    setOpen(true);
  };

  const uploadPdf = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post(`/uploads/pdf?kind=${kind}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.url;
  };

  const handleUploadPdf = async (e, kind) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) {
      toast.error("Selectează un PDF");
      return;
    }
    const t = toast.loading(`Se încarcă ${kind === "aviz" ? "Aviz Bio" : "Fișă SSA"}...`);
    try {
      const url = await uploadPdf(file, kind);
      setForm((f) => ({ ...f, [kind === "aviz" ? "aviz_pdf_url" : "ssa_pdf_url"]: url }));
      toast.dismiss(t);
      toast.success(`${kind === "aviz" ? "Aviz Bio" : "Fișă SSA"} încărcat`);
    } catch (err) {
      toast.dismiss(t);
      toast.error("Upload eșuat: " + (err.response?.data?.detail || err.message));
    }
  };

  const buildAuthedUrl = (url) => {
    if (!url) return "";
    const tok = localStorage.getItem("token");
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const sep = url.includes("?") ? "&" : "?";
    return `${backend}${url}${sep}auth=${encodeURIComponent(tok || "")}`;
  };

  // Upload + save Aviz/SSA la nivel de produs (stoc) — persistă între mișcări
  const saveStockPdf = async (patch) => {
    if (!activeId) return;
    try {
      const { data } = await api.patch(`/stocks/${activeId}`, patch);
      setStocks((prev) => prev.map((s) => (s.id === activeId ? { ...s, ...data } : s)));
      toast.success("Document actualizat");
    } catch (e) {
      toast.error("Eroare la salvare document");
    }
  };
  const handleUploadStockPdf = async (e, kind) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) {
      toast.error("Selectează un PDF");
      return;
    }
    const t = toast.loading("Se încarcă PDF-ul...");
    try {
      const url = await uploadPdf(file, kind);
      toast.dismiss(t);
      await saveStockPdf({ [kind === "aviz" ? "aviz_pdf_url" : "ssa_pdf_url"]: url });
    } catch (err) {
      toast.dismiss(t);
      toast.error("Upload eșuat");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!activeId) return;
    if (!form.cantitate || Number(form.cantitate) <= 0) {
      toast.error("Introdu o cantitate validă");
      return;
    }
    setSaving(true);
    try {
      if (editingMov) {
        await api.patch(`/stocks/${activeId}/movements/${editingMov.id}`, {
          ...form,
          cantitate: parseFloat(form.cantitate),
        });
        toast.success("Mișcare modificată. Stoc recalculat.");
      } else {
        await api.post(`/stocks/${activeId}/movements`, {
          ...form,
          cantitate: parseFloat(form.cantitate),
        });
        toast.success(form.tip === "intrare" ? "Intrare înregistrată" : "Ieșire înregistrată");
      }
      setOpen(false);
      setEditingMov(null);
      setForm(EMPTY);
      loadMovements(activeId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Ștergi mișcarea din ${m.data} (${m.tip} — ${m.cantitate} ${active?.unitate || ""})?`)) return;
    try {
      await api.delete(`/stocks/${activeId}/movements/${m.id}`);
      toast.success("Șters. Stoc recalculat.");
      loadMovements(activeId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    }
  };

  const exportExcel = async () => {
    if (!activeId) return;
    try {
      const params = new URLSearchParams();
      if (startDate) params.set("start", startDate);
      if (endDate) params.set("end", endDate);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const url = `${API_BASE}/stocks/${activeId}/fisa/export${qs}`;
      const res = await fetch(url, {
        credentials: "include",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("Export eșuat");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      const range = (startDate || endDate) ? `_${startDate || "start"}_${endDate || "azi"}` : "";
      link.download = `fisa_magazie_${(active?.denumire || "stoc").replace(/[^A-Za-z0-9._-]+/g, "_")}${range}.xlsx`;
      link.click();
    } catch {
      toast.error("Nu s-a putut descărca fișa");
    }
  };

  return (
    <div className="space-y-6" data-testid="fise-magazie-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="label-eyebrow">Inventar</p>
          <h1 className="font-display font-black text-4xl tracking-tight mt-2 flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-gold-deep" />
            Fișă de Magazie
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Câte o fișă pentru fiecare substanță — exact ca în Excel. Alege substanța din tab-uri.
          </p>
        </div>
        {active && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={exportExcel} data-testid="fise-export-btn">
              <Download className="w-4 h-4 mr-2" /> Export Excel
            </Button>
            {isAdmin && (
              <Button
                onClick={() => { setForm({ ...EMPTY, tip: "intrare", unitate: active?.unitate || "buc" }); setOpen(true); }}
                className="btn-gold hover:btn-gold"
                data-testid="fise-add-intrare-btn"
              >
                <ArrowDownToLine className="w-4 h-4 mr-2" /> Intrare
              </Button>
            )}
            {isAdmin && (
              <Button
                onClick={() => { setForm({ ...EMPTY, tip: "iesire", unitate: active?.unitate || "buc" }); setOpen(true); }}
                variant="outline"
                className="border-red-200 text-red-700 hover:bg-red-50"
                data-testid="fise-add-iesire-btn"
              >
                <ArrowUpFromLine className="w-4 h-4 mr-2" /> Ieșire
              </Button>
            )}
          </div>
        )}
      </header>

      {/* Date range filter */}
      {stocks.length > 0 && (
        <div className="bg-white border border-border rounded-md p-4 flex flex-col lg:flex-row lg:items-end gap-3">
          <div className="flex-1 grid grid-cols-2 gap-3 max-w-md">
            <div>
              <Label className="label-eyebrow flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" /> De la
              </Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-2 h-10"
                data-testid="fise-date-start"
              />
            </div>
            <div>
              <Label className="label-eyebrow flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" /> Până la
              </Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-2 h-10"
                data-testid="fise-date-end"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" variant="outline" onClick={() => setPreset("today")} data-testid="preset-today">Azi</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset("month")} data-testid="preset-month">Luna asta</Button>
            {(startDate || endDate) && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPreset("clear")}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="preset-clear"
              >
                <X className="w-3 h-3 mr-1" /> Șterge filtru
              </Button>
            )}
          </div>
          {(startDate || endDate) && (
            <p className="text-xs text-muted-foreground lg:ml-auto">
              Filtrat: <b>{startDate || "început"}</b> → <b>{endDate || "azi"}</b>
            </p>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Se încarcă stocurile...</p>
      ) : stocks.length === 0 ? (
        <div className="bg-white border border-border rounded-md p-12 text-center">
          <Package className="w-10 h-10 mx-auto text-muted-foreground" />
          <p className="mt-3 font-medium">Niciun stoc înregistrat</p>
          <p className="text-sm text-muted-foreground mt-1">
            Adaugă întâi substanțe din pagina „Stocuri".
          </p>
        </div>
      ) : (
        <>
          {/* Excel-like sheet tabs (bottom-style, but placed on top for usability) */}
          <div className="bg-white border border-border rounded-t-md overflow-x-auto">
            <div className="flex min-w-max border-b border-border bg-slate-50">
              {stocks.map((s) => {
                const isActive = s.id === activeId;
                const low = s.prag_alarma > 0 && s.cantitate <= s.prag_alarma;
                return (
                  <button
                    key={s.id}
                    onClick={() => setActiveId(s.id)}
                    data-testid={`sheet-tab-${s.id}`}
                    className={
                      "relative px-4 py-2.5 text-xs font-semibold uppercase tracking-wider border-r border-border transition-colors whitespace-nowrap " +
                      (isActive
                        ? "bg-white text-gold-deep border-t-2 border-t-gold -mt-px"
                        : "text-slate-600 hover:bg-white hover:text-foreground")
                    }
                  >
                    {low && (
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 mr-1.5 align-middle" />
                    )}
                    {s.denumire}
                  </button>
                );
              })}
            </div>

            {active && (
              <div className="px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm border-b border-border bg-white">
                <span className="font-semibold text-lg">{active.denumire}</span>
                <span className="text-muted-foreground">
                  U.M.: <b className="text-foreground">{active.unitate || "—"}</b>
                </span>
                <span className="text-muted-foreground">
                  Lot curent: <b className="text-foreground">{active.aviz_lot || "—"}</b>
                </span>
                <span className="text-muted-foreground">
                  Stoc: <b className={(active.cantitate ?? 0) <= (active.prag_alarma ?? 0) && (active.prag_alarma ?? 0) > 0 ? "text-red-600" : "text-foreground"}>
                    {active.cantitate ?? 0} {active.unitate || ""}
                  </b>
                </span>
                <span className="ml-auto text-xs">
                  <span className="text-emerald-700 font-semibold">↓ Intrări: {totals.intrari.toFixed(2)}</span>
                  <span className="mx-3 text-slate-300">|</span>
                  <span className="text-red-700 font-semibold">↑ Ieșiri: {totals.iesiri.toFixed(2)}</span>
                </span>
              </div>
            )}

            {/* PDF-uri Aviz Bio + SSA pe produs (persistă între mișcări) */}
            {active && (
              <div className="px-4 py-2 flex flex-wrap items-center gap-2 border-b border-border bg-slate-50/60">
                <span className="label-eyebrow mr-2">Documente produs:</span>
                <StockPdfBadge
                  label="Aviz Bio"
                  url={active.aviz_pdf_url}
                  isAdmin={isAdmin}
                  onUpload={(e) => handleUploadStockPdf(e, "aviz")}
                  onRemove={() => saveStockPdf({ aviz_pdf_url: "" })}
                  buildAuthedUrl={buildAuthedUrl}
                  color="emerald"
                />
                <StockPdfBadge
                  label="Fișă SSA"
                  url={active.ssa_pdf_url}
                  isAdmin={isAdmin}
                  onUpload={(e) => handleUploadStockPdf(e, "ssa")}
                  onRemove={() => saveStockPdf({ ssa_pdf_url: "" })}
                  buildAuthedUrl={buildAuthedUrl}
                  color="blue"
                />
              </div>
            )}
          </div>

          {/* Excel-like table */}
          <div className="bg-white border border-border border-t-0 rounded-b-md overflow-x-auto">
            <table className="w-full text-sm min-w-[900px] border-collapse">
              <thead>
                <tr className="bg-[#B08D57] text-white">
                  <th className="px-3 py-2 border border-[#8a6c40] font-bold uppercase text-xs tracking-wider">Data</th>
                  <th className="px-3 py-2 border border-[#8a6c40] font-bold uppercase text-xs tracking-wider">Fabricare</th>
                  <th className="px-3 py-2 border border-[#8a6c40] font-bold uppercase text-xs tracking-wider">Expirare</th>
                  <th className="px-3 py-2 border border-[#8a6c40] font-bold uppercase text-xs tracking-wider text-right">Intrări</th>
                  <th className="px-3 py-2 border border-[#8a6c40] font-bold uppercase text-xs tracking-wider text-right">Ieșiri</th>
                  <th className="px-3 py-2 border border-[#8a6c40] font-bold uppercase text-xs tracking-wider text-right">Stoc</th>
                  <th className="px-3 py-2 border border-[#8a6c40] font-bold uppercase text-xs tracking-wider">Nr. Lot</th>
                  <th className="px-3 py-2 border border-[#8a6c40] font-bold uppercase text-xs tracking-wider">Aviz Bio</th>
                  <th className="px-3 py-2 border border-[#8a6c40] font-bold uppercase text-xs tracking-wider">U.M.</th>
                  <th className="px-3 py-2 border border-[#8a6c40] font-bold uppercase text-xs tracking-wider">Firma</th>
                  {isAdmin && <th className="px-3 py-2 border border-[#8a6c40] font-bold uppercase text-xs tracking-wider" />}
                </tr>
              </thead>
              <tbody>
                {loadingMov ? (
                  <tr>
                    <td colSpan={isAdmin ? 11 : 10} className="px-3 py-6 text-center text-muted-foreground border border-slate-200">
                      Se încarcă mișcările...
                    </td>
                  </tr>
                ) : (
                  <>
                    {/* Sold precedent — reportat din perioada anterioară filtrului */}
                    {soldInitial !== null && soldInitial !== undefined && (
                      <tr className="bg-amber-50/70 border-y border-amber-200" data-testid="sold-initial-row">
                        <td className="px-3 py-1.5 border border-slate-200 font-mono text-xs text-center italic text-amber-900">
                          {soldInitialData || "—"}
                        </td>
                        <td className="px-3 py-1.5 border border-slate-200" colSpan={4}>
                          <span className="text-xs italic text-amber-900 font-semibold">Sold reportat (din perioada anterioară)</span>
                        </td>
                        <td className="px-3 py-1.5 border border-slate-200 font-mono text-right font-bold text-amber-900">
                          {Number(soldInitial).toFixed(2)}
                        </td>
                        <td className="px-3 py-1.5 border border-slate-200" colSpan={isAdmin ? 5 : 4} />
                      </tr>
                    )}

                    {movements.length === 0 ? (
                      <tr>
                        <td colSpan={isAdmin ? 11 : 10} className="px-3 py-8 text-center text-muted-foreground border border-slate-200">
                          Nicio mișcare în perioada aleasă. Soldul rămâne la ultima fișă.
                        </td>
                      </tr>
                    ) : (
                      movements.map((m, idx) => {
                        const expSoon = (() => {
                          if (!m.data_expirare) return false;
                          const d = new Date(m.data_expirare);
                          if (isNaN(d)) return false;
                          const diff = (d - new Date()) / (1000 * 60 * 60 * 24);
                          return diff < 60; // sub 60 zile
                        })();
                        return (
                        <tr
                          key={m.id}
                          data-testid={`sheet-row-${m.id}`}
                          className={idx % 2 === 0 ? "bg-white hover:bg-amber-50/40" : "bg-slate-50/60 hover:bg-amber-50/40"}
                        >
                          <td className="px-3 py-1.5 border border-slate-200 font-mono text-xs text-center">
                            {m.data || "—"}
                          </td>
                          <td className="px-3 py-1.5 border border-slate-200 font-mono text-xs text-center">
                            {m.data_fabricare || "—"}
                          </td>
                          <td className={"px-3 py-1.5 border border-slate-200 font-mono text-xs text-center " + (expSoon ? "text-red-700 font-semibold" : "")}>
                            {m.data_expirare || "—"}
                          </td>
                          <td className="px-3 py-1.5 border border-slate-200 font-mono text-right text-emerald-700">
                            {m.tip === "intrare" ? Number(m.cantitate).toFixed(2) : ""}
                          </td>
                          <td className="px-3 py-1.5 border border-slate-200 font-mono text-right text-red-700">
                            {m.tip === "iesire" ? Number(m.cantitate).toFixed(2) : ""}
                          </td>
                          <td className="px-3 py-1.5 border border-slate-200 font-mono text-right font-semibold">
                            {Number(m.stoc_rezultat).toFixed(2)}
                          </td>
                          <td className="px-3 py-1.5 border border-slate-200 font-mono text-xs text-center">
                            {m.nr_lot || "—"}
                          </td>
                          <td className="px-3 py-1.5 border border-slate-200 font-mono text-xs text-center">
                            {m.aviz || "—"}
                          </td>
                          <td className="px-3 py-1.5 border border-slate-200 text-xs text-center">
                            {m.unitate || active?.unitate || "—"}
                          </td>
                          <td className="px-3 py-1.5 border border-slate-200 text-xs">
                            <div className="font-medium">{m.firma || "—"}</div>
                            {m.referinta_numar && (
                              <div className="text-[11px] text-muted-foreground">{m.referinta_numar}</div>
                            )}
                            {m.observatii && (
                              <div className="text-[11px] text-muted-foreground italic">{m.observatii}</div>
                            )}
                          </td>
                          {isAdmin && (
                            <td className="px-2 py-1.5 border border-slate-200 text-center w-20">
                              <div className="flex items-center gap-1 justify-center">
                                {m.aviz_pdf_url && (
                                  <a
                                    href={buildAuthedUrl(m.aviz_pdf_url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-emerald-700 hover:text-emerald-900"
                                    title="Vezi Aviz Bio"
                                    data-testid={`view-aviz-${m.id}`}
                                  >
                                    <FileText className="w-3.5 h-3.5" />
                                  </a>
                                )}
                                {m.ssa_pdf_url && (
                                  <a
                                    href={buildAuthedUrl(m.ssa_pdf_url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-700 hover:text-blue-900"
                                    title="Vezi Fișă SSA"
                                    data-testid={`view-ssa-${m.id}`}
                                  >
                                    <Paperclip className="w-3.5 h-3.5" />
                                  </a>
                                )}
                                <button
                                  type="button"
                                  onClick={() => setViewMov(m)}
                                  className="text-gold-deep hover:text-gold"
                                  data-testid={`view-mov-${m.id}`}
                                  title="Vezi detalii mișcare"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openEditMov(m)}
                                  className="text-blue-600 hover:text-blue-800"
                                  data-testid={`edit-mov-${m.id}`}
                                  title="Modifică mișcare"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                        );
                      })
                    )}

                    {/* Sold la zi (azi) — reportat dacă nu s-a făcut nimic azi */}
                    {ultimaMiscareData && ultimaMiscareData < todayISO() && (
                      <tr className="bg-emerald-50/70 border-y border-emerald-200" data-testid="sold-la-zi-row">
                        <td className="px-3 py-1.5 border border-slate-200 font-mono text-xs text-center italic text-emerald-900 font-semibold">
                          {todayISO()}
                        </td>
                        <td className="px-3 py-1.5 border border-slate-200" colSpan={4}>
                          <span className="text-xs italic text-emerald-900 font-semibold">
                            Sold la zi (fără mișcări din {ultimaMiscareData})
                          </span>
                        </td>
                        <td className="px-3 py-1.5 border border-slate-200 font-mono text-right font-bold text-emerald-900">
                          {Number(soldCurent ?? 0).toFixed(2)}
                        </td>
                        <td className="px-3 py-1.5 border border-slate-200" colSpan={isAdmin ? 5 : 4} />
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditingMov(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editingMov ? `Modifică ${editingMov.tip === "intrare" ? "intrare" : "ieșire"}` : (form.tip === "intrare" ? "Adaugă intrare (marfă primită)" : "Adaugă ieșire manuală")}
            </DialogTitle>
            <DialogDescription>
              Fișă: <b>{active?.denumire}</b>
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-eyebrow">Tip *</Label>
                <select
                  value={form.tip}
                  onChange={(e) => setForm({ ...form, tip: e.target.value })}
                  className="mt-2 h-11 w-full border border-input rounded-md bg-white px-3 text-sm"
                  data-testid="fise-mov-tip"
                >
                  <option value="intrare">Intrare</option>
                  <option value="iesire">Ieșire</option>
                </select>
              </div>
              <div>
                <Label className="label-eyebrow">Data *</Label>
                <Input
                  type="date"
                  value={form.data}
                  onChange={(e) => setForm({ ...form, data: e.target.value })}
                  className="mt-2 h-11"
                  required
                  data-testid="fise-mov-data"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-eyebrow">Data fabricare</Label>
                <Input
                  type="date"
                  value={form.data_fabricare}
                  onChange={(e) => setForm({ ...form, data_fabricare: e.target.value })}
                  className="mt-2 h-11"
                  data-testid="fise-mov-data-fabricare"
                />
              </div>
              <div>
                <Label className="label-eyebrow">Data expirare</Label>
                <Input
                  type="date"
                  value={form.data_expirare}
                  onChange={(e) => setForm({ ...form, data_expirare: e.target.value })}
                  className="mt-2 h-11"
                  data-testid="fise-mov-data-expirare"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="label-eyebrow">Cantitate *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.cantitate}
                  onChange={(e) => setForm({ ...form, cantitate: e.target.value })}
                  className="mt-2 h-11"
                  placeholder="0"
                  required
                  data-testid="fise-mov-qty"
                />
              </div>
              <div>
                <Label className="label-eyebrow">U.M. *</Label>
                <select
                  value={form.unitate}
                  onChange={(e) => setForm({ ...form, unitate: e.target.value })}
                  className="mt-2 h-11 w-full border border-input rounded-md bg-white px-3 text-sm"
                  data-testid="fise-mov-unitate"
                >
                  {UNITATI_OPTIONS.map((u) => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-3">
                <Label className="label-eyebrow">Nr. Lot</Label>
                <Input
                  value={form.nr_lot}
                  onChange={(e) => setForm({ ...form, nr_lot: e.target.value })}
                  className="mt-2 h-11"
                  placeholder="ex: L2026-01"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-eyebrow">Aviz Bio</Label>
                <Input
                  value={form.aviz}
                  onChange={(e) => setForm({ ...form, aviz: e.target.value })}
                  className="mt-2 h-11"
                  placeholder="ex: 1234/2026"
                />
              </div>
              <div>
                <Label className="label-eyebrow">Referință</Label>
                <Input
                  value={form.referinta}
                  onChange={(e) => setForm({ ...form, referinta: e.target.value })}
                  className="mt-2 h-11"
                  placeholder="ex: Aviz furnizor 45"
                />
              </div>
            </div>
            <div>
              <Label className="label-eyebrow">Firma / Furnizor / Client</Label>
              <Input
                value={form.firma}
                onChange={(e) => setForm({ ...form, firma: e.target.value })}
                className="mt-2 h-11"
                placeholder="ex: Bayer, Client X..."
              />
            </div>
            <div>
              <Label className="label-eyebrow">Observații</Label>
              <Input
                value={form.observatii}
                onChange={(e) => setForm({ ...form, observatii: e.target.value })}
                className="mt-2 h-11"
                placeholder="Note interne..."
              />
            </div>

            {/* PDF attachments — Aviz Bio & Fișă SSA */}
            <div className="border-t border-border pt-3 mt-1">
              <p className="label-eyebrow mb-2">Documente atașate (PDF)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <AttachPdfBox
                  label="Aviz Bio"
                  url={form.aviz_pdf_url}
                  onUpload={(e) => handleUploadPdf(e, "aviz")}
                  onRemove={() => setForm({ ...form, aviz_pdf_url: "" })}
                  buildAuthedUrl={buildAuthedUrl}
                  testid="upload-aviz"
                />
                <AttachPdfBox
                  label="Fișă SSA (Securitate)"
                  url={form.ssa_pdf_url}
                  onUpload={(e) => handleUploadPdf(e, "ssa")}
                  onRemove={() => setForm({ ...form, ssa_pdf_url: "" })}
                  buildAuthedUrl={buildAuthedUrl}
                  testid="upload-ssa"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
              <Button
                type="submit"
                disabled={saving}
                className="btn-gold hover:btn-gold"
                data-testid="fise-save-mov-btn"
              >
                {saving ? "Se salvează..." : "Salvează"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Vizualizare detalii mișcare */}
      <Dialog open={!!viewMov} onOpenChange={(v) => { if (!v) setViewMov(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Eye className="w-5 h-5 text-gold-deep" />
              Detalii mișcare
            </DialogTitle>
            <DialogDescription>
              {active?.denumire} · {viewMov?.data}
            </DialogDescription>
          </DialogHeader>
          {viewMov && (
            <div className="space-y-3 text-sm">
              <DetailRow label="Data" value={viewMov.data} />
              <DetailRow label="Data fabricare" value={viewMov.data_fabricare || "—"} />
              <DetailRow label="Data expirare" value={viewMov.data_expirare || "—"} />
              <DetailRow label="Tip" value={
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${viewMov.tip === "intrare" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                  {viewMov.tip === "intrare" ? "↓ INTRARE" : "↑ IEȘIRE"}
                </span>
              } />
              <DetailRow label="Cantitate" value={`${Number(viewMov.cantitate).toFixed(2)} ${viewMov.unitate || active?.unitate || ""}`} />
              <DetailRow label="Stoc rezultat" value={`${Number(viewMov.stoc_rezultat).toFixed(2)} ${viewMov.unitate || active?.unitate || ""}`} />
              <DetailRow label="Nr. Lot" value={viewMov.nr_lot || "—"} />
              <DetailRow label="Aviz Bio" value={viewMov.aviz || "—"} />
              <DetailRow label="Firma / Referință" value={viewMov.firma || "—"} />
              {viewMov.referinta_numar && <DetailRow label="Document" value={viewMov.referinta_numar} />}
              {viewMov.observatii && <DetailRow label="Observații" value={viewMov.observatii} />}
              {viewMov.created_by_name && <DetailRow label="Înregistrat de" value={viewMov.created_by_name} />}
              {viewMov.created_at && <DetailRow label="Data înregistrării" value={new Date(viewMov.created_at).toLocaleString("ro-RO")} />}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewMov(null)}>Închide</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AttachPdfBox({ label, url, onUpload, onRemove, buildAuthedUrl, testid }) {
  if (url) {
    return (
      <div className="border border-emerald-300 bg-emerald-50/50 rounded-md p-2.5 text-xs">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold text-emerald-900 truncate">{label}</span>
          <button type="button" onClick={onRemove} className="text-red-600 hover:text-red-800" title="Elimină">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <a
          href={buildAuthedUrl(url)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 mt-1 text-emerald-800 hover:text-emerald-900 underline"
          data-testid={`${testid}-view`}
        >
          <ExternalLink className="w-3 h-3" /> Deschide PDF
        </a>
      </div>
    );
  }
  return (
    <label
      className="border border-dashed border-slate-300 hover:border-gold hover:bg-gold-soft/30 rounded-md p-2.5 cursor-pointer text-xs flex flex-col items-center justify-center gap-1 transition-colors text-center min-h-[70px]"
      data-testid={testid}
    >
      <Paperclip className="w-4 h-4 text-slate-500" />
      <span className="text-slate-700 font-medium">Atașează {label}</span>
      <span className="text-[10px] text-slate-500">PDF, max 15 MB</span>
      <input type="file" accept="application/pdf,.pdf" onChange={onUpload} className="hidden" />
    </label>
  );
}


function DetailRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border pb-2 last:border-0">
      <span className="label-eyebrow shrink-0">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

function StockPdfBadge({ label, url, isAdmin, onUpload, onRemove, buildAuthedUrl, color }) {
  const colorMap = {
    emerald: { border: "border-emerald-400", bg: "bg-emerald-50", txt: "text-emerald-800", hover: "hover:bg-emerald-100" },
    blue:    { border: "border-blue-400",    bg: "bg-blue-50",    txt: "text-blue-800",    hover: "hover:bg-blue-100" },
  };
  const c = colorMap[color] || colorMap.emerald;
  if (url) {
    return (
      <div className={`inline-flex items-center gap-2 border ${c.border} ${c.bg} rounded-md px-2.5 py-1 text-xs`}>
        <a
          href={buildAuthedUrl(url)}
          target="_blank"
          rel="noopener noreferrer"
          className={`${c.txt} font-semibold hover:underline inline-flex items-center gap-1`}
          data-testid={`stock-pdf-view-${label.toLowerCase().replace(/\s/g, "-")}`}
        >
          <FileText className="w-3.5 h-3.5" /> Vezi {label}
        </a>
        {isAdmin && (
          <button type="button" onClick={onRemove} className="text-red-600 hover:text-red-800" title="Elimină">
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  }
  if (!isAdmin) {
    return <span className="text-xs text-slate-400 italic">Fără {label}</span>;
  }
  return (
    <label className={`inline-flex items-center gap-1.5 border border-dashed ${c.border} ${c.hover} rounded-md px-2.5 py-1 text-xs cursor-pointer ${c.txt}`}>
      <Paperclip className="w-3.5 h-3.5" />
      <span className="font-medium">Atașează {label}</span>
      <input type="file" accept="application/pdf,.pdf" onChange={onUpload} className="hidden" />
    </label>
  );
}
